// Layer 3 (Transformation) — pure derivations for the Downtime tab (v19 M11).
// No React, no engine mutations; every exported function takes seed data (or
// nothing) and returns plain values.

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { addMinutes, diffMinutes, parseTime } from '@/versions/v1/shared/clock.derive';
import {
  BASIS_END,
  DOWNTIME_ITEMS,
  DOWNTIME_STATES,
  DRAW_END,
  DRAW_START,
  REASON_CODES,
  type DowntimeItemId,
  type ItemState,
  type ItemStateKind,
  type ReasonCodeEvent,
} from './downtime.seed';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const STATE_ORDER: readonly ItemStateKind[] = [
  'running_design',
  'running_degraded',
  'idle_upstream',
  'idle_downstream',
  'offline_breakdown',
  'offline_shutdown',
];

export const STATE_LABELS: Record<ItemStateKind, string> = {
  running_design: 'Running at design',
  running_degraded: 'Running degraded',
  idle_upstream: 'Idle — upstream',
  idle_downstream: 'Idle — downstream',
  offline_breakdown: 'Breakdown',
  offline_shutdown: 'Planned shutdown',
};

/** v19 M11 §3 — a running item still counts as "up" for MTBF/MTTR even when
 *  performance is degraded; only breakdown and idle reduce availability. */
const IDLE_STATES = new Set<ItemStateKind>(['idle_upstream', 'idle_downstream']);

/** Month-4 ramp forecast — 1,390 stpd against the plant's 1,800 stpd
 *  (75 t/h) design rate (`kpi-ore-tpd`, bunker-hill-planning-seed.ts:201;
 *  design throughput per brief-a20-common). Every maintainable item on this
 *  page sits on the same single ore stream — grinding is the constraint and
 *  every downstream bank only ever sees what grinding delivers — so this is
 *  the Performance basis for the whole plant, not a per-item invented rate. */
const ACTUAL_PLANT_RATE_TPH = 1390 / 24;
const NOMINAL_RATE_TPH = 75;

/** Performance = tonnes moved during uptime ÷ (uptime hours × nominal rate),
 *  not a flat invented factor — the ratio happens to be the same for every
 *  item because the plant is one ore stream, but it is derived from the
 *  month-4 forecast against design, not hardcoded as a KPI-level constant. */
function performancePctForUptime(uptimeMinutes: number): number {
  const uptimeHours = uptimeMinutes / 60;
  if (uptimeHours <= 0) return 0;
  const tonnesAtUptime = uptimeHours * ACTUAL_PLANT_RATE_TPH;
  return (tonnesAtUptime / (uptimeHours * NOMINAL_RATE_TPH)) * 100;
}

function dayTickLabel(t: SeriesTime): string {
  const d = parseTime(t);
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTH_ABBR[d.getUTCMonth()]}`;
}

/** Minutes of overlap between [aStart,aEnd) and [bStart,bEnd). */
function overlapMinutes(aStart: SeriesTime, aEnd: SeriesTime, bStart: SeriesTime, bEnd: SeriesTime): number {
  const start = diffMinutes(aStart, bStart) > 0 ? aStart : bStart;
  const end = diffMinutes(aEnd, bEnd) < 0 ? aEnd : bEnd;
  const minutes = diffMinutes(end, start);
  return minutes > 0 ? minutes : 0;
}

/** Merge chronologically adjacent intervals that share a state (the 30-day
 *  background tail authors consecutive `offline_breakdown` parts back to
 *  back with no separating interval) — otherwise one real stoppage counts
 *  as two events and inflates breakdown count while deflating MTBF/MTTR. */
export function mergeAdjacentSameState(intervals: readonly ItemState[]): ItemState[] {
  const sorted = [...intervals].sort((a, b) => diffMinutes(a.start, b.start));
  const merged: ItemState[] = [];
  for (const row of sorted) {
    const prev = merged[merged.length - 1];
    if (prev && prev.state === row.state && diffMinutes(row.start, prev.end) === 0) {
      merged[merged.length - 1] = { ...prev, end: row.end };
    } else {
      merged.push({ ...row });
    }
  }
  return merged;
}

function itemIntervals(itemId: DowntimeItemId): ItemState[] {
  return mergeAdjacentSameState(DOWNTIME_STATES.filter((row) => row.itemId === itemId));
}

export type ItemKpis = {
  itemId: DowntimeItemId;
  label: string;
  days: number;
  availabilityPct: number;
  performancePct: number;
  qualityPct: number;
  oeePct: number;
  runHours: number;
  breakdownCount: number;
  repairHours: number;
  mtbfHours: number;
  mttrHours: number;
};

type Totals = {
  designMinutes: number;
  degradedMinutes: number;
  idleMinutes: number;
  shutdownMinutes: number;
  breakdownMinutes: number;
  breakdownCount: number;
  windowMinutes: number;
};

function totalsFor(intervals: readonly ItemState[], windowStart: SeriesTime, windowEnd: SeriesTime): Totals {
  const totals: Totals = {
    designMinutes: 0,
    degradedMinutes: 0,
    idleMinutes: 0,
    shutdownMinutes: 0,
    breakdownMinutes: 0,
    breakdownCount: 0,
    windowMinutes: diffMinutes(windowEnd, windowStart),
  };
  for (const row of intervals) {
    const minutes = overlapMinutes(row.start, row.end, windowStart, windowEnd);
    if (minutes <= 0) continue;
    if (row.state === 'running_design') totals.designMinutes += minutes;
    else if (row.state === 'running_degraded') totals.degradedMinutes += minutes;
    else if (IDLE_STATES.has(row.state)) totals.idleMinutes += minutes;
    else if (row.state === 'offline_shutdown') totals.shutdownMinutes += minutes;
    else if (row.state === 'offline_breakdown') {
      totals.breakdownMinutes += minutes;
      // Count a breakdown once per interval that overlaps the window at all,
      // not per overlapping minute.
      totals.breakdownCount += 1;
    }
  }
  return totals;
}

type DerivedMetrics = Omit<ItemKpis, 'itemId' | 'label'>;

function metricsFromTotals(days: number, totals: Totals): DerivedMetrics {
  const uptimeMinutes = totals.designMinutes + totals.degradedMinutes;
  const plannedProductionMinutes = totals.windowMinutes - totals.shutdownMinutes;
  const availabilityPct = plannedProductionMinutes > 0 ? (uptimeMinutes / plannedProductionMinutes) * 100 : 0;
  const performancePct = performancePctForUptime(uptimeMinutes);
  const qualityPct = 100; // [syn] no reject/rework signal modelled at this scope
  const oeePct = (availabilityPct / 100) * (performancePct / 100) * (qualityPct / 100) * 100;
  const mtbfHours = totals.breakdownCount > 0 ? uptimeMinutes / 60 / totals.breakdownCount : uptimeMinutes / 60;
  const mttrHours = totals.breakdownCount > 0 ? totals.breakdownMinutes / 60 / totals.breakdownCount : 0;

  return {
    days,
    availabilityPct,
    performancePct,
    qualityPct,
    oeePct,
    runHours: uptimeMinutes / 60,
    breakdownCount: totals.breakdownCount,
    repairHours: totals.breakdownMinutes / 60,
    mtbfHours,
    mttrHours,
  };
}

/** Per-item OEE/availability/performance/quality + MTBF/MTTR over the
 *  trailing `days` (default 30) ending at the current production-day close. */
export function itemKpis(itemId: DowntimeItemId, days = 30): ItemKpis {
  const item = DOWNTIME_ITEMS.find((row) => row.id === itemId);
  const windowStart = addMinutes(BASIS_END, -days * 24 * 60);
  const totals = totalsFor(itemIntervals(itemId), windowStart, BASIS_END);
  return { itemId, label: item?.label ?? itemId, ...metricsFromTotals(days, totals) };
}

export type PlantKpis = DerivedMetrics;

/** Plant-level roll-up — sums raw minutes across every maintainable item
 *  before dividing, not an average of per-item percentages. */
export function plantKpis(days = 30): PlantKpis {
  const windowStart = addMinutes(BASIS_END, -days * 24 * 60);
  const totals: Totals = {
    designMinutes: 0,
    degradedMinutes: 0,
    idleMinutes: 0,
    shutdownMinutes: 0,
    breakdownMinutes: 0,
    breakdownCount: 0,
    windowMinutes: 0,
  };
  for (const { id } of DOWNTIME_ITEMS) {
    const itemTotals = totalsFor(itemIntervals(id), windowStart, BASIS_END);
    totals.designMinutes += itemTotals.designMinutes;
    totals.degradedMinutes += itemTotals.degradedMinutes;
    totals.idleMinutes += itemTotals.idleMinutes;
    totals.shutdownMinutes += itemTotals.shutdownMinutes;
    totals.breakdownMinutes += itemTotals.breakdownMinutes;
    totals.breakdownCount += itemTotals.breakdownCount;
    // Plant "planned production time" basis — one item-window's length times
    // item count, since every item shares the same calendar window.
    totals.windowMinutes = itemTotals.windowMinutes;
  }
  const plantWindowMinutes = totals.windowMinutes * DOWNTIME_ITEMS.length;
  return metricsFromTotals(days, { ...totals, windowMinutes: plantWindowMinutes });
}

export type TimelineSegment = {
  itemId: DowntimeItemId;
  state: ItemStateKind;
  startPct: number;
  widthPct: number;
  start: SeriesTime;
  end: SeriesTime;
  neighbour?: string;
  note?: string;
  workOrderId?: string;
};

/** Percentage-positioned segments for one item's timeline row, clipped to
 *  [from,to) (default the drawn 01–07 Jun week). */
export function timelineSegments(
  itemId: DowntimeItemId,
  from: SeriesTime = DRAW_START,
  to: SeriesTime = DRAW_END,
): TimelineSegment[] {
  const totalMinutes = diffMinutes(to, from);
  if (totalMinutes <= 0) return [];
  const segments: TimelineSegment[] = [];
  for (const row of itemIntervals(itemId)) {
    const clippedStart = diffMinutes(row.start, from) > 0 ? row.start : from;
    const clippedEnd = diffMinutes(row.end, to) < 0 ? row.end : to;
    const width = diffMinutes(clippedEnd, clippedStart);
    if (width <= 0) continue;
    segments.push({
      itemId,
      state: row.state,
      startPct: (diffMinutes(clippedStart, from) / totalMinutes) * 100,
      widthPct: (width / totalMinutes) * 100,
      start: clippedStart,
      end: clippedEnd,
      neighbour: row.neighbour,
      note: row.note,
      workOrderId: row.workOrderId,
    });
  }
  return segments;
}

export type DayTick = { at: SeriesTime; label: string; leftPct: number };

/** One tick per calendar day across [from,to) — the timeline's date axis. */
export function dayTicks(from: SeriesTime = DRAW_START, to: SeriesTime = DRAW_END): DayTick[] {
  const totalMinutes = diffMinutes(to, from);
  if (totalMinutes <= 0) return [];
  const days = Math.round(totalMinutes / (24 * 60));
  return Array.from({ length: days }, (_, index) => {
    const at = addMinutes(from, index * 24 * 60);
    return { at, label: dayTickLabel(at), leftPct: (index / days) * 100 };
  });
}

const REVIEW_RANK: Record<ReasonCodeEvent['reviewStatus'], number> = {
  pending: 0,
  recoded: 1,
  confirmed: 2,
};

/** Reason-code review queue, pending rows first (v19 M11 §1's daily review loop). */
export function reasonQueue(): readonly ReasonCodeEvent[] {
  return [...REASON_CODES].sort((a, b) => REVIEW_RANK[a.reviewStatus] - REVIEW_RANK[b.reviewStatus]);
}

export function itemLabel(itemId: DowntimeItemId): string {
  return DOWNTIME_ITEMS.find((row) => row.id === itemId)?.label ?? itemId;
}

export function formatHours(hours: number): string {
  return `${hours.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`;
}

export function formatPct(pct: number): string {
  return `${pct.toFixed(1)}%`;
}

export function reasonDurationHours(row: ReasonCodeEvent): number {
  return diffMinutes(row.end, row.start) / 60;
}

/** "04 Jun 06:00" — the reason-code queue's Event column. */
export function formatEventStart(at: SeriesTime): string {
  const d = parseTime(at);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hm = at.split('T')[1] ?? '';
  return `${day} ${MONTH_ABBR[d.getUTCMonth()]} ${hm}`;
}

/** "04 Jun 06:00–09:30" — a timeline segment's hover title. */
export function formatEventWindow(start: SeriesTime, end: SeriesTime): string {
  const d = parseTime(start);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const startHm = start.split('T')[1] ?? '';
  const endHm = end.split('T')[1] ?? '';
  return `${day} ${MONTH_ABBR[d.getUTCMonth()]} ${startHm}–${endHm}`;
}
